import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, ChevronDown, ChevronUp, Copy, Download } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import AppShell from "@/components/app/AppShell";
import PhotoGallery from "@/components/app/PhotoGallery";
import { useAuth } from "@/contexts/AuthContext";

const dayFromDate = (d) => {
    if (!d) return "";
    try {
        return new Date(d + "T00:00:00").toLocaleDateString(undefined, { weekday: "long" });
    } catch {
        return "";
    }
};

const Field = ({ label, value, testId }) => (
    <div>
        <div className="label">{label}</div>
        <div className="input input-locked min-h-[44px]" data-testid={testId}>
            {value != null && value !== "" ? value : <span className="text-slate-400">—</span>}
        </div>
    </div>
);

const SectionCard = ({ title, subtitle, children, className = "", ...rest }) => (
    <section className={`glass-card ${className}`} {...rest}>
        <div className="mb-5">
            <div className="section-title">{title}</div>
            {subtitle && <div className="section-subtitle">{subtitle}</div>}
        </div>
        {children}
    </section>
);

const WorkItemReadOnly = ({ item, index, reportId }) => {
    const [open, setOpen] = useState(false);
    return (
        <div className="glass-card !py-4" data-testid={`view-work-item-${index}`}>
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="w-full flex items-center justify-between text-left"
                data-testid={`view-work-item-toggle-${index}`}
            >
                <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                        <span className="text-[11px] uppercase tracking-wider text-slate-400">
                            #{index + 1}
                        </span>
                        <div className="text-[15px] font-semibold text-slate-900 truncate">
                            {item.title || <span className="text-slate-400 italic">Untitled</span>}
                        </div>
                    </div>
                    {(item.location || "").trim() && (
                        <div className="text-[12px] text-slate-500 mt-0.5">{item.location}</div>
                    )}
                </div>
                <div className="text-slate-400">
                    {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </div>
            </button>
            {open && (
                <div className="mt-4 flex flex-col gap-4">
                    <div>
                        <div className="label">Description of work</div>
                        <div className="input input-locked min-h-[80px] whitespace-pre-wrap items-start py-3 leading-relaxed">
                            {item.description_of_work || <span className="text-slate-400">—</span>}
                        </div>
                    </div>
                    {(item.materials_used || "").trim() && (
                        <div>
                            <div className="label">Materials used</div>
                            <div className="input input-locked min-h-[60px] whitespace-pre-wrap items-start py-3">
                                {item.materials_used}
                            </div>
                        </div>
                    )}
                    {(item.photos || []).length > 0 && (
                        <div>
                            <div className="label">Photos</div>
                            <PhotoGallery
                                reportId={reportId}
                                photos={item.photos}
                                readOnly
                                testIdPrefix={`view-work-item-photos-${index}`}
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const ReportViewPage = () => {
    const { id } = useParams();
    const nav = useNavigate();
    const { user: authUser } = useAuth();
    const [report, setReport] = useState(null);
    const [error, setError] = useState("");
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/reports/${id}`);
                // If the report is a draft AND belongs to the current user, jump to edit.
                // Admins viewing someone else's draft stay in read-only view.
                const isOwner = authUser && authUser !== false && data.owner_user_id === authUser.id;
                if (data.status !== "certified" && isOwner) {
                    nav(`/reports/${id}/edit`, { replace: true });
                    return;
                }
                setReport(data);
            } catch (err) {
                if (err?.response?.status === 404) {
                    nav("/reports", { replace: true });
                    return;
                }
                setError(formatApiError(err));
            }
        })();
    }, [id, nav, authUser]);

    const copyLink = async () => {
        if (!report?.pdf_url) return;
        try {
            await navigator.clipboard.writeText(report.pdf_url);
            setCopied(true);
            setTimeout(() => setCopied(false), 1600);
        } catch {
            window.prompt("Copy this link:", report.pdf_url);
        }
    };

    if (!report) {
        return (
            <AppShell>
                <div className="glass-card text-[13px] text-slate-500">{error || "Loading…"}</div>
            </AppShell>
        );
    }

    const emailSent = !!report.pdf_url;
    const coverPhotos = report.cover_photos || [];
    const workItems = report.work_items || [];

    return (
        <AppShell>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                <div className="flex items-center gap-4">
                    <Link
                        to="/reports"
                        className="btn btn-secondary btn-sm btn-icon"
                        aria-label="Back to reports"
                        data-testid="view-back-button"
                    >
                        <ArrowLeft size={16} strokeWidth={2} />
                    </Link>
                    <div>
                        <div className="flex items-center gap-2 text-[13px] text-slate-500">
                            {report.status === "certified" ? (
                                <>
                                    <span className="chip chip-certified">
                                        <Check size={11} strokeWidth={2.5} />
                                        Certified
                                    </span>
                                    {report.certified_at && (
                                        <span>
                                            ·{" "}
                                            {new Date(report.certified_at).toLocaleString(undefined, {
                                                year: "numeric", month: "short", day: "2-digit",
                                                hour: "2-digit", minute: "2-digit",
                                            })}
                                        </span>
                                    )}
                                </>
                            ) : (
                                <span className="chip chip-draft" data-testid="view-draft-chip">Draft (read-only)</span>
                            )}
                        </div>
                        <h1 className="text-3xl font-semibold text-slate-900 mt-1" data-testid="view-page-title">
                            {report.job_number || "Report"}
                        </h1>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    {report.status === "certified" && (
                        <>
                            <button onClick={copyLink} className="btn btn-secondary" data-testid="view-copy-link">
                                <Copy size={16} strokeWidth={2} />
                                {copied ? "Copied" : "Copy link"}
                            </button>
                            <a
                                href={report.pdf_url}
                                target="_blank"
                                rel="noreferrer"
                                className="btn btn-primary"
                                data-testid="view-download-pdf"
                            >
                                <Download size={16} strokeWidth={2} />
                                Download PDF
                            </a>
                        </>
                    )}
                </div>
            </div>

            <div className="flex flex-col gap-5">
                {/* Job Info */}
                <SectionCard title="Job Info" subtitle={report.day_of_week || dayFromDate(report.report_date)}>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Field label="Job number" value={report.job_number} testId="view-job-number" />
                        <div className="md:col-span-2">
                            <Field label="Location" value={report.location} testId="view-location" />
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        <Field label="Date" value={report.report_date} testId="view-report-date" />
                        <Field
                            label="Day of week"
                            value={report.day_of_week || dayFromDate(report.report_date)}
                        />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        <Field label="Weather" value={report.weather} testId="view-weather" />
                        <Field
                            label="Temperature"
                            value={
                                report.temperature?.value != null
                                    ? `${report.temperature.value}°${report.temperature.unit || "F"}`
                                    : "—"
                            }
                            testId="view-temperature"
                        />
                    </div>
                </SectionCard>

                {/* Work Info */}
                <SectionCard
                    title="Work Info"
                    subtitle={`${workItems.length} work item${workItems.length === 1 ? "" : "s"}`}
                >
                    {workItems.length === 0 ? (
                        <div className="text-[13px] text-slate-400 italic">No work items.</div>
                    ) : (
                        <div className="flex flex-col gap-3">
                            {workItems.map((w, i) => (
                                <WorkItemReadOnly key={w.id || i} item={w} index={i} reportId={id} />
                            ))}
                        </div>
                    )}
                </SectionCard>

                {/* Site Photos (only if any) */}
                {coverPhotos.length > 0 && (
                    <SectionCard title="Site Photos">
                        <PhotoGallery
                            reportId={id}
                            photos={coverPhotos}
                            readOnly
                            testIdPrefix="view-cover-photos"
                        />
                    </SectionCard>
                )}

                {/* Employees */}
                <SectionCard
                    title="Employees"
                    subtitle={
                        report.crew_total_hours != null
                            ? `Crew total ${report.crew_total_hours} h`
                            : "Crew total —"
                    }
                    className="!p-0"
                >
                    <div className="px-6 pt-2">
                        <Field
                            label="Contractor work hours"
                            value={
                                report.contractor_hours?.start && report.contractor_hours?.end
                                    ? `${report.contractor_hours.start} – ${report.contractor_hours.end}`
                                    : "—"
                            }
                            testId="view-contractor-hours"
                        />
                    </div>
                    <div className="overflow-x-auto px-2 mt-3">
                        <table className="g-table" data-testid="view-employees-table">
                            <thead>
                                <tr>
                                    <th>Position</th>
                                    <th>Employee name</th>
                                    <th>Time in</th>
                                    <th>Time out</th>
                                    <th className="text-right">Regular</th>
                                    <th className="text-right">OT</th>
                                    <th className="text-right">Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(report.employees || []).map((row, idx) => (
                                    <tr key={idx}>
                                        <td>
                                            <span className="chip chip-neutral">{row.position || "—"}</span>
                                        </td>
                                        <td>{row.name || "—"}</td>
                                        <td className="tabular-nums">{row.time_in || "—"}</td>
                                        <td className="tabular-nums">{row.time_out || "—"}</td>
                                        <td className="tabular-nums text-right">
                                            {row.regular_hours != null ? row.regular_hours : "—"}
                                        </td>
                                        <td className="tabular-nums text-right">
                                            {row.overtime_hours != null ? row.overtime_hours : "—"}
                                        </td>
                                        <td className="tabular-nums text-right">
                                            {row.total_hours != null ? row.total_hours : "—"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td colSpan={4} className="text-right text-slate-600">Crew total</td>
                                    <td className="text-right tabular-nums">
                                        {report.crew_regular_hours != null ? report.crew_regular_hours : "—"}
                                    </td>
                                    <td className="text-right tabular-nums">
                                        {report.crew_overtime_hours != null ? report.crew_overtime_hours : "—"}
                                    </td>
                                    <td className="text-right tabular-nums">
                                        {report.crew_total_hours != null ? `${report.crew_total_hours} h` : "—"}
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </SectionCard>

                {/* Certification — only for certified reports (admins may view a draft in read-only) */}
                {report.status === "certified" && (
                <SectionCard title="Certification" subtitle="Locked">
                    <div className="relative">
                        <div
                            className="pointer-events-none absolute inset-0 flex items-center justify-center z-10"
                            aria-hidden
                            data-testid="certified-stamp"
                        >
                            <span className="stamp-certified">Certified</span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="flex flex-col gap-3">
                                <Field label="PM email" value={report.pm_email} />
                                {(report.cc_emails || []).length > 0 && (
                                    <div
                                        className="text-[13px] text-slate-500 flex flex-wrap items-center gap-1.5"
                                        data-testid="view-cc-emails"
                                    >
                                        <span>Copied:</span>
                                        {report.cc_emails.map((e, i) => (
                                            <span
                                                key={i}
                                                className="inline-flex items-center rounded-full bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-[12px] text-emerald-800"
                                                data-testid={`view-cc-chip-${i}`}
                                            >
                                                {e}
                                            </span>
                                        ))}
                                    </div>
                                )}
                                <div>
                                    <div className="label">Certification statement</div>
                                    <div className="input input-locked whitespace-normal items-start py-3 min-h-[64px] leading-relaxed">
                                        I certify that the information contained in this Daily Field Report is true and accurate to the best of my knowledge.
                                    </div>
                                </div>
                            </div>
                            <div>
                                <div className="label">Signature</div>
                                <div className="rounded-[10px] border border-[rgba(15,23,42,0.1)] bg-white h-[140px] flex items-center justify-center overflow-hidden">
                                    {report.signature_png_base64 ? (
                                        <img
                                            alt="Signature"
                                            className="max-h-full max-w-full"
                                            src={
                                                report.signature_png_base64.startsWith("data:")
                                                    ? report.signature_png_base64
                                                    : `data:image/png;base64,${report.signature_png_base64}`
                                            }
                                            data-testid="view-signature-image"
                                        />
                                    ) : (
                                        <span className="text-[13px] text-slate-400">No signature on file</span>
                                    )}
                                </div>
                                <div className="hint">
                                    Certified by user {report.certified_by ? report.certified_by.slice(0, 8) : "—"}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div
                        className="mt-6 pt-4 border-t border-[var(--divider)] flex flex-wrap items-center gap-2 text-[13px]"
                        data-testid="view-email-status"
                    >
                        <span className="text-slate-500">Email to</span>
                        <span className="font-medium text-slate-900">{report.pm_email || "—"}</span>
                        <span className="text-slate-400">·</span>
                        <span className={emailSent ? "text-emerald-700" : "text-red-500"}>
                            {emailSent
                                ? "Sent — link above delivers the PDF"
                                : "Failed — copy link and forward manually"}
                        </span>
                    </div>
                </SectionCard>
                )}
            </div>
        </AppShell>
    );
};

export default ReportViewPage;
